import { useDeferredValue, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { saveBdcDelivery } from "../../../services";
import { BdcShipmentDetail } from "../../../types/bdc.type";
import { lineText, pcs } from "../format";
import { CARD_SX, SCROLL_SX } from "../styles";
import Tag from "../Tag";
import { DeliveryDraft, draftErrors, itemDelivered, manualItems, manualRows } from "./delivery";

/** Rows drawn at once; WB01 has ~2,200 stores per product — narrow with the search. */
const ROWS_SHOWN = 300;

/**
 * แก้ยอดส่งรายร้าน (Manual) — pick a product, type each store's delivery (0 … the store's PO
 * pieces). The draft survives switching product, so one save can cover several; only cells that
 * really change are sent (PUT …/delivery, mode manual).
 */
export default function ManualEditor({
  detail,
  onCancel,
  onSaved,
}: Readonly<{
  detail: BdcShipmentDetail;
  onCancel: () => void;
  onSaved: () => void;
}>) {
  const products = useMemo(
    () =>
      detail.items
        .filter((item) => detail.stores.some((store) => (store.qty?.[item.barcode] ?? 0) > 0))
        .sort((a, b) => a.item_code.localeCompare(b.item_code)),
    [detail],
  );
  const [barcode, setBarcode] = useState(products[0]?.barcode ?? "");
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [draft, setDraft] = useState<DeliveryDraft>({});
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => manualRows(detail.stores, barcode, deferredSearch), [detail, barcode, deferredSearch]);
  const errors = useMemo(() => draftErrors(draft, detail.stores), [draft, detail]);
  const items = useMemo(() => manualItems(draft, detail.stores), [draft, detail]);
  const errorCount = Object.keys(errors).length;
  const product = products.find((p) => p.barcode === barcode);

  const save = async () => {
    if (items.length === 0 || errorCount > 0) return;
    setSaving(true);
    try {
      await saveBdcDelivery(detail.header.po_number, { mode: "manual", items });
      onSaved();
    } catch {
      // the interceptor shows the server's reason (422 names the bad store/barcode)
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={CARD_SX} data-testid="bdc-manual-editor">
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1.25 }}>
        <TextField
          select
          size="small"
          label="สินค้า"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          sx={{ minWidth: 280 }}
        >
          {products.map((p) => (
            <MenuItem key={p.barcode} value={p.barcode}>
              {p.item_code} {p.name} · ส่ง {pcs(itemDelivered(p))}/{pcs(p.qty)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder="ค้นหา สาย / รหัสร้าน / ชื่อร้าน"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <Typography className="sub">ลดได้อย่างเดียว · 0 ถึงยอดสั่งของร้าน · ช่องว่าง = ไม่แก้</Typography>
      </Stack>
      <Box sx={{ ...SCROLL_SX, maxHeight: "calc(100vh - 380px)" }}>
        <table className="bdc-t">
          <thead>
            <tr>
              <th>สาย</th>
              <th>จุดส่ง</th>
              <th>รหัส</th>
              <th>ร้าน</th>
              <th className="n">สั่ง (PO)</th>
              <th className="n">ส่งตอนนี้</th>
              <th className="n">ส่งใหม่</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, ROWS_SHOWN).map((row) => {
              const error = errors[row.key];
              return (
                <tr key={row.key}>
                  <td>{lineText(row.line_no)}</td>
                  <td>{lineText(row.stop_no)}</td>
                  <td className="mono">{row.store_code}</td>
                  <td>{row.store_name}</td>
                  <td className="n sub">{pcs(row.po)}</td>
                  <td className="n">{pcs(row.delivered)}</td>
                  <td className="n" style={{ width: 120 }}>
                    <TextField
                      size="small"
                      value={draft[row.key] ?? ""}
                      placeholder={String(row.delivered)}
                      error={Boolean(error)}
                      title={error ?? undefined}
                      onChange={(e) => {
                        const text = e.target.value;
                        setDraft((current) => {
                          const next = { ...current };
                          if (text.trim() === "") delete next[row.key];
                          else next[row.key] = text;
                          return next;
                        });
                      }}
                      slotProps={{
                        htmlInput: {
                          inputMode: "numeric",
                          "aria-label": `ส่งใหม่ ${row.store_code}`,
                          style: { textAlign: "right", padding: "4px 8px" },
                        },
                      }}
                    />
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="sub" style={{ textAlign: "center" }}>
                  ไม่พบร้านที่สั่ง{product ? ` ${product.name}` : ""}
                </td>
              </tr>
            )}
            {rows.length > ROWS_SHOWN && (
              <tr>
                <td colSpan={7} className="sub">
                  แสดง {ROWS_SHOWN} จาก {pcs(rows.length)} ร้าน — ค้นหาสายหรือรหัสร้านเพื่อดูส่วนที่เหลือ
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1.25, borderTop: "1px solid #E3E5EA" }}>
        {errorCount > 0 ? (
          <Alert severity="error" sx={{ py: 0 }}>
            มี {pcs(errorCount)} ช่องที่ไม่ถูกต้อง (ต้องเป็นจำนวนเต็ม 0 ถึงยอดสั่งของร้าน)
          </Alert>
        ) : (
          <Tag tone={items.length > 0 ? "warn" : "mut"}>แก้ {pcs(items.length)} ช่อง</Tag>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={onCancel}>
          ยกเลิก
        </Button>
        <Button variant="contained" disabled={items.length === 0 || errorCount > 0} loading={saving} onClick={save}>
          บันทึกยอดส่ง
        </Button>
      </Stack>
    </Card>
  );
}
